"use client";

import type { SecurityDepositMode } from "@fm/api";
import { CREDIT_CARD_NOTICE, holdRenewalNoticeFor } from "./depositCopy";

export interface DepositConsentCheckboxProps {
  amount: number;
  currency?: string;
  mode: SecurityDepositMode;
  acceptCash?: boolean;
  /** True when the deposit hold needs a credit card (no debit/prepaid). */
  creditCardRequired?: boolean;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    amount,
  );

const explanationFor = (
  mode: SecurityDepositMode,
  amount: number,
  currency: string,
  acceptCash?: boolean,
): string => {
  const formatted = formatAmount(amount, currency);

  switch (mode) {
    case "after_checkout":
      return `After booking, a ${formatted} hold will be placed on your card. This is not a charge — funds are released after your rental ends, unless damages, fees, or policy violations apply.`;

    case "at_checkin":
      return `A ${formatted} hold will be placed automatically on your card at the start of your pickup date${
        acceptCash ? " (cash may also be accepted)" : ""
      }. This is not a charge — funds are released after your rental ends, unless damages, fees, or policy violations apply.`;

    case "manual":
      return `A ${formatted} security deposit will be requested separately by the host. It is not collected during this checkout.`;

    default:
      return `Security deposit: ${formatAmount(amount, currency)}.`;
  }
};

export function DepositConsentCheckbox({
  amount,
  currency = "USD",
  mode,
  acceptCash,
  creditCardRequired = false,
  checked,
  disabled = false,
  onChange,
  className = "",
}: DepositConsentCheckboxProps) {
  if (mode === "disabled" || amount <= 0) return null;

  const explanation = explanationFor(mode, amount, currency, acceptCash);
  const formatted = formatAmount(amount, currency);
  const renewalNotice = holdRenewalNoticeFor(mode);

  return (
    <div
      className={`rounded-lg border bg-white p-4 ${className}`}
      data-testid="deposit-consent-checkbox"
    >
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          id="deposit-consent"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        <span className="flex-1 text-sm">
          <span className="block font-semibold mb-1">
            I authorize the {formatted} security deposit.
          </span>
          <span className="block text-muted-foreground text-xs">
            {explanation}
          </span>
          {renewalNotice && (
            <span
              className="block mt-1 text-muted-foreground text-xs"
              data-testid="deposit-hold-renewal-notice"
            >
              {renewalNotice}
            </span>
          )}
          {creditCardRequired && (
            <span
              className="block mt-2 text-xs font-medium"
              data-testid="deposit-credit-card-notice"
            >
              {CREDIT_CARD_NOTICE}
            </span>
          )}
        </span>
      </label>
    </div>
  );
}
